import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { AccountDetails, CostCenters, TaxCode } from "@/apis/gql/types";
import { EditBookingForm } from "@/components/shared/bookings/edit-booking-form";
import { RowsSelected } from "@/components/shared/tables/ActionBar";
import { Button } from "@/components/ui/button";
import { Shortcut } from "@/components/ui/shortcut";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { BulkEditActionBar } from "./BulkEditActionBar";

export interface BulkEditActionBarProps {
  table: any;
  accounts: AccountDetails[] | undefined;
  taxCodes: TaxCode[] | undefined;
  costCenters: CostCenters[] | undefined;
  handleCombinedAccountChange: (value: any) => void;
  handleCombinedTaxCodeChange: (value: any) => void;
  handleCombinedCostCentersChange: (value: any) => void;
  handleDeleteSelected: () => Promise<void>;
}

const editLineItemFormSchema = z.object({
  accountId: z.string().max(255).nullable().optional(),
  taxCodeId: z.string().max(255).nullable().optional(),
  costCenterId: z.string().max(255).nullable().optional(),
});

type EditLineItemFrom = z.infer<typeof editLineItemFormSchema>;

const LEGACY_MODE = false;

export const BulkEditLineItemActionBar = ({
  accounts,
  taxCodes,
  table,
  costCenters,
  handleCombinedAccountChange,
  handleCombinedTaxCodeChange,
  handleDeleteSelected,
  handleCombinedCostCentersChange,
}: BulkEditActionBarProps) => {
  const { t } = useTranslation();

  const handleSubmit = async ({
    accountId,
    taxCodeId,
    costCenterId,
  }: EditLineItemFrom) => {
    if (accountId !== undefined) {
      handleCombinedAccountChange(accountId);
    }

    if (taxCodeId !== undefined) {
      handleCombinedTaxCodeChange(taxCodeId);
    }

    if (costCenterId !== undefined) {
      handleCombinedCostCentersChange(costCenterId);
    }
  };

  useHotkeys(["mod+d"], () => {
    handleDeleteSelected();
  });

  if (LEGACY_MODE) {
    return (
      <BulkEditActionBar
        table={table}
        handleCombinedAccountChange={handleCombinedAccountChange}
        accounts={accounts}
        costCenters={costCenters}
        handleCombinedTaxCodeChange={handleCombinedTaxCodeChange}
        handleCombinedCostCentersChange={handleCombinedCostCentersChange}
        taxCodes={taxCodes}
        handleDeleteSelected={handleDeleteSelected}
      />
    );
  }

  const selectedRowModel = table?.getSelectedRowModel();
  const _rows = selectedRowModel?.rows ?? [];

  const selectedRowEditValues = _rows.map((row: any) => ({
    accountId: row.original.accountId,
    taxCodeId: row.original.taxCodeId,
  }));

  const emptyValues = {
    accountId: undefined,
    taxCodeId: undefined,
  };

  const { accountId, taxCodeId } = selectedRowEditValues[0] ?? emptyValues;

  const allAreTheSame =
    selectedRowEditValues.length > 0
      ? selectedRowEditValues.every(
          (item: EditLineItemFrom) =>
            item.accountId === accountId && item.taxCodeId === taxCodeId,
        )
      : false;

  // TODO fix this. The internal state of the form component is getting in the way...
  const initialValues = allAreTheSame ? { accountId, taxCodeId } : emptyValues;

  return (
    <div className="flex flex-row gap-2 items-center">
      <RowsSelected
        totalRowsLength={table?.getRowCount()}
        selectedRowsLength={table?.getSelectedRowModel()?.rows?.length}
        onDeselectAll={() => table.resetRowSelection(true)}
      />
      <EditBookingForm onSubmit={handleSubmit} />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button variant="outline" onClick={handleDeleteSelected}>
              {t("delete")}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Shortcut.ShortcutRoot>
              <Shortcut.ShortcutCommand className="bg-muted-foreground" />
              <Shortcut.ShortcutHotKey className="bg-muted-foreground">
                D
              </Shortcut.ShortcutHotKey>
            </Shortcut.ShortcutRoot>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
